import {Event} from 'shared/events/Event';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useCallback} from 'react';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import {useAppContext} from 'contexts/ApplicationContext';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {useConfirmVariationsMutation} from 'components/customer/Job/store/jobApi';
import {useAppSelector} from 'store/customer';
import {
    selectRemovedBenchIds,
    selectRemovedExtraVariationIds,
    selectRemovedJobCabinetIds,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {shallowEqual} from 'react-redux';
import {useNotificationContext} from 'contexts/NotificationContext';
import {genericMessageHandler} from 'shared/helpers';

export const useConfirmVariation = (): Event => {
    const {notify} = useNotificationContext();
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;
    const {isConfirmationPage} = useIsConfirmationPage();
    const [confirmVariations] = useConfirmVariationsMutation();
    const removedProductVariations = useAppSelector(
        selectRemovedJobCabinetIds,
        shallowEqual
    );
    const removedBenchVariations = useAppSelector(
        selectRemovedBenchIds,
        shallowEqual
    );
    const removedExtraVariations = useAppSelector(
        selectRemovedExtraVariationIds,
        shallowEqual
    );

    const action = useCallback((): Promise<boolean> => {
        return new Promise((resolve) => {
            // only run this from confirmation page
            if (isConfirmationPage) {
                // only if job is pending variation confirmation
                if (
                    job.status == JOB_STATUS.JOB_PENDING_CONFIRMATION &&
                    job.accepted
                ) {
                    // and variations are not yet accpeted
                    if (!job.variationsAllAccepted) {
                        confirmVariations({
                            jobId: job.id,
                            variations: {
                                job_cabinet_variations:
                                    removedProductVariations,
                                btm_variations: removedBenchVariations,
                                extra_variations: removedExtraVariations,
                            },
                        })
                            .then(() => {
                                resolve(true);
                            })
                            .catch(() => {
                                genericMessageHandler(notify, {
                                    message:
                                        'There was error confirming variations. Please try again later',
                                });
                                resolve(false);
                            });
                    }
                }
            }
            resolve(true);
        });
    }, [
        job,
        isConfirmationPage,
        userProfile,
        removedProductVariations,
        removedBenchVariations,
        removedExtraVariations,
        confirmVariations,
    ]);

    return {
        action,
    };
};
